import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { secureLog } from '@/lib/security';

export interface PreloadedTemplate {
  id: string;
  template_name: string;
  target_date: string | null;
  countdown_enabled: boolean;
  template_type: 'sample' | 'event' | 'blog';
  is_published: boolean;
  phases: Array<{
    id: string;
    phase_title: string;
    display_order: number;
    items: Array<{
      id: string;
      item_text: string;
      notes: string | null;
      is_ready: boolean;
      is_bundled: boolean;
      is_secured: boolean;
    }>;
  }>;
}

export interface PreloadedSection {
  id: string;
  section_name: string;
  target_date: string | null;
  countdown_enabled: boolean;
  display_order: number;
  phases: Array<{
    id: string;
    phase_title: string;
    display_order: number;
    items: Array<{
      id: string;
      item_text: string;
      notes: string | null;
      is_ready: boolean;
      is_bundled: boolean;
      is_secured: boolean;
    }>;
  }>;
}

interface UseDataPreloaderReturn {
  templates: PreloadedTemplate[];
  userSections: PreloadedSection[];
  isLoading: boolean;
  hasLoadedUserSections: boolean;
  refetchTemplates: () => Promise<void>;
  refetchUserSections: () => Promise<void>;
}

// Simple in-memory cache for templates (they change infrequently)
// Module-level cache persists across all hook instances and re-renders
let templatesCache: { data: PreloadedTemplate[] | null, timestamp: number } = {
  data: null,
  timestamp: 0
};

export const useDataPreloader = (userId?: string): UseDataPreloaderReturn => {
  const [templates, setTemplates] = useState<PreloadedTemplate[]>(templatesCache.data || []);
  const [userSections, setUserSections] = useState<PreloadedSection[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [hasLoadedUserSections, setHasLoadedUserSections] = useState(false);
  
  const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  const loadTemplates = async () => {
    try {
      // Check cache first
      const now = Date.now();
      if (templatesCache.data && (now - templatesCache.timestamp) < CACHE_DURATION) {
        setTemplates(templatesCache.data);
        return;
      }

      secureLog.debug('Loading templates data...');

      // Single JOIN query to get all templates with phases and items
      const { data: templatesData, error: templatesError } = await supabase
        .from('templates')
        .select(`
          id,
          template_name,
          target_date,
          countdown_enabled,
          display_order,
          template_type,
          is_published,
          template_phases!inner(
            id,
            phase_title,
            display_order,
            template_items(
              id,
              item_text,
              notes,
              is_ready,
              is_bundled,
              is_secured,
              display_order
            )
          )
        `)
        .eq('is_active', true)
        .order('display_order');

      if (templatesError) {
        secureLog.error('Error loading templates:', templatesError);
        throw templatesError;
      }

      if (!templatesData || templatesData.length === 0) {
        secureLog.debug('No active templates found');
        setTemplates([]);
        return;
      }

      // Transform the nested JOIN data into hierarchical structure
      const transformedTemplates = templatesData.map(template => ({
        id: template.id,
        template_name: template.template_name,
        target_date: template.target_date,
        countdown_enabled: template.countdown_enabled,
        template_type: template.template_type,
        is_published: template.is_published,
        phases: template.template_phases
          .sort((a, b) => a.display_order - b.display_order)
          .map(phase => ({
            id: phase.id,
            phase_title: phase.phase_title,
            display_order: phase.display_order,
            items: (phase.template_items || [])
              .sort((a, b) => a.display_order - b.display_order)
          }))
      }));

      secureLog.debug(`Successfully loaded ${transformedTemplates.length} templates with phases and items`);

      // Update cache and state
      templatesCache.data = transformedTemplates;
      templatesCache.timestamp = now;
      setTemplates(transformedTemplates);
    } catch (error) {
      secureLog.error('Error preloading templates', error);
      setTemplates([]);
    }
  };

  const loadUserSections = async () => {
    if (!userId) {
      setUserSections([]);
      setHasLoadedUserSections(true);
      return;
    }

    try {
      const { data: sectionsData, error: sectionsError } = await supabase
        .from('checklist_sections')
        .select(`
          id,
          section_name,
          target_date,
          countdown_enabled,
          display_order,
          checklist_phases(
            id,
            phase_title,
            display_order,
            checklist_items(
              id,
              item_text,
              notes,
              is_ready,
              is_bundled,
              is_secured,
              display_order
            )
          )
        `)
        .eq('user_id', userId)
        .order('display_order');

      if (sectionsError) {
        throw sectionsError;
      }

      // Transform the flat joined data into the expected hierarchical structure
      const transformedSections = (sectionsData || []).map(section => ({
        id: section.id,
        section_name: section.section_name,
        target_date: section.target_date,
        countdown_enabled: section.countdown_enabled,
        display_order: section.display_order,
        phases: (section.checklist_phases || [])
          .sort((a, b) => a.display_order - b.display_order)
          .map(phase => ({
            id: phase.id,
            phase_title: phase.phase_title,
            display_order: phase.display_order,
            items: (phase.checklist_items || [])
              .sort((a, b) => a.display_order - b.display_order)
          }))
      }));

      setUserSections(transformedSections);
      setHasLoadedUserSections(true);
    } catch (error) {
      secureLog.error('Error preloading user sections', error);
      setUserSections([]);
      setHasLoadedUserSections(true);
    }
  };

  // Initial load of both templates and user sections (deferred, non-blocking)
  useEffect(() => {
    // Set loading state IMMEDIATELY when userId changes, not inside the callback
    setIsLoading(true);
    setHasLoadedUserSections(false);
    
    // Defer template loading until idle (saves 200-300ms on mobile LCP)
    const idleCallback = 'requestIdleCallback' in window 
      ? (window as any).requestIdleCallback 
      : (cb: () => void) => setTimeout(cb, 1000);
    
    idleCallback(() => {
      // Parallel data fetching for better performance
      Promise.all([
        loadTemplates(),
        userId ? loadUserSections() : Promise.resolve()
      ]).finally(() => {
        setIsLoading(false);
        setIsInitialLoad(false);
      });
    });
  }, [userId]); // Re-run when userId changes


  // Listen for admin data changes
  useEffect(() => {
    const handleAdminDataChange = () => {
      // Invalidate cache and reload templates
      templatesCache.data = null;
      templatesCache.timestamp = 0;
      loadTemplates();
    };
    
    window.addEventListener('adminDataChanged', handleAdminDataChange);
    return () => window.removeEventListener('adminDataChanged', handleAdminDataChange);
  }, []);

  const refetchUserSections = async () => {
    await loadUserSections();
  };

  const refetchTemplates = async () => {
    await loadTemplates();
  };

  return {
    templates,
    userSections,
    isLoading,
    hasLoadedUserSections,
    refetchTemplates,
    refetchUserSections,
  };
};